import React, { useEffect, useState } from 'react';
import '../App.css';

// API URL based on environment
const API_ENDPOINT = '/api/send-email';

function Contact() {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service: 'general'
  });
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Update scroll progress indicator
    const updateScrollProgress = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      const scrollBar = document.querySelector('.scroll-progress');
      if (scrollBar) {
        scrollBar.style.width = scrolled + '%';
      }
    };

    // Add event listener
    window.addEventListener('scroll', updateScrollProgress);
    
    // Clean up
    return () => window.removeEventListener('scroll', updateScrollProgress);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    // Make API call to the correct endpoint based on environment
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        setIsSubmitting(false);
        
        if (data.success) {
          setSubmitSuccess(true);
          
          // Reset form after submission
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            message: '',
            service: 'general'
          });
          
          // Reset success message after 5 seconds
          setTimeout(() => {
            setSubmitSuccess(false);
          }, 5000);
        } else {
          setErrorMessage(data.error || 'Failed to send message. Please try again.');
        }
      })
      .catch(error => {
        setIsSubmitting(false);
        setErrorMessage('Network error. Please check your connection and try again.');
      });
  };

  return (
    <div className="w-full">
      {/* Scroll Progress Indicator */}
      <div className="scroll-progress"></div>
      
      {/* Hero Section */}
      <header className="bg-white">
        <div className="container">
          <div className="flex justify-between items-center py-8 flex-col-mobile">
            <div>
              <h1 className="mb-4">
                Get in <span className="text-flame">Touch</span>
              </h1>
              <p className="mb-8 text-gray-600">Let's discuss how we can ignite your next project</p>
            </div>
            <div>
              <img src="/assets/video-call.svg" alt="Contact Us" style={{width: '100%', height: '100%'}}/>
            </div>
          </div>
        </div>
      </header>

      {/* Contact Form Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="flex justify-between items-start flex-col-mobile">
            <div className="contact-info">
              <h2 className="mb-6">Contact <span className="text-flame">Information</span></h2>
              <div className="contact-method">
                <h3>Email Us</h3>
                <p className="text-gray-600">business@onfiresoft.com</p>
              </div>
              <div className="contact-method">
                <h3>Call Us</h3>
                <p className="text-gray-600">+(60) 167103412</p>
              </div>
              
              <div className="contact-method">
                <h3>Hours</h3>
                <p className="text-gray-600">
                  Monday - Friday: 9AM - 6PM<br />
                  Saturday & Sunday: Closed
                </p>
              </div>
              
              <div className="social-links">
                <h3>Connect With Us</h3>
                <div className="social-icons">
                  <a href="https://linkedin.com/company/onfire-software-solutions" className="social-icon">
                    <span>LinkedIn</span>
                  </a>
                  <a href="https://x.com/onfiresoft" className="social-icon">
                    <span>Twitter</span>
                  </a>
                  <a href="https://www.xiaohongshu.com/user/profile/6028c912000000000101fd04" className="social-icon">
                    <span>XHS</span>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="contact-form-container">
              <h2 className="mb-6">Send us a <span className="text-flame">Message</span></h2>
              
              {submitSuccess ? (
                <div className="success-message">
                  <h3>Thank you for reaching out!</h3>
                  <p>We've received your message and will get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input 
                      type="text" 
                      id="company" 
                      name="company" 
                      value={formData.company} 
                      onChange={handleChange} 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="service">Service Interested In</label>
                    <select 
                      id="service" 
                      name="service" 
                      value={formData.service} 
                      onChange={handleChange}
                    >
                      <option value="general">General Inquiry</option>
                      <option value="software">Custom Software Development</option>
                      <option value="design">UX/UI Design</option>
                      <option value="devops">DevOps Services</option>
                      <option value="ai">AI & Machine Learning</option>
                      <option value="blockchain">Blockchain Development</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      value={formData.message} 
                      onChange={handleChange} 
                      required
                      rows="5"
                    ></textarea>
                  </div>
                  
                  {errorMessage && (
                    <div className="error-message">{errorMessage}</div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="btn" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      {/* <section className="section bg-white">
        <div className="container">
          <h2 className="section-title">Find <span className="text-flame">Us</span></h2>
          <div className="map-container">
            <div className="map-placeholder">
              <p className="text-center text-gray-600">
                Interactive map would be displayed here.
                <br />
                Integration with Google Maps or similar service required.
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <h2 className="section-title">Frequently <span className="text-flame">Asked Questions</span></h2>
          <div className="faq-container">
            <div className="faq-item">
              <h3>What is your typical process for new projects?</h3>
              <p className="text-gray-600">
                We start with a discovery call to understand your needs, followed by a proposal with 
                scope and timeline. After approval, we move into design, development, testing, and deployment 
                phases, with regular check-ins throughout.
              </p>
            </div>
            <div className="faq-item">
              <h3>How long does a typical project take?</h3>
              <p className="text-gray-600">
                Project timelines vary based on complexity and scope. Simple websites might take 4-6 weeks, 
                while complex applications can take 3-6 months or more. We'll provide a detailed timeline 
                during the proposal phase.
              </p>
            </div>
            <div className="faq-item">
              <h3>Do you offer maintenance after the project is complete?</h3>
              <p className="text-gray-600">
                Yes, we offer various maintenance packages to ensure your software remains secure, updated, 
                and functioning optimally. We can customize a plan based on your specific needs.
              </p>
            </div>
            <div className="faq-item">
              <h3>What technologies do you specialize in?</h3>
              <p className="text-gray-600">
                We have expertise in a wide range of technologies including React, Angular, Vue, Node.js, 
                Python, .NET, Java, and various mobile development frameworks. We select the best technology 
                stack based on your project requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-flame text-white py-16 text-center">
        <div className="container">
          <h2 className="mb-4">Ready to Get Started?</h2>
          <p className="mb-8">Take the first step toward transforming your business with OnFire</p>
          <button className="btn btn-white">Schedule a Consultation</button>
        </div>
      </section>
    </div>
  );
}

export default Contact; 